import { z } from "zod";

// Single source of truth for French validation messages.
// Call registerFrenchErrorMap() once at app startup (e.g. in main.tsx) before schemas are evaluated.
// You can extend or override specific issue codes via the optional overrides parameter.

let registered = false;

export type FrenchErrorOverrides = Partial<
  Record<
    z.ZodIssueCode,
    (issue: unknown, ctx: { defaultError: string }) => string
  >
>;

// Accept the broader optional message issue type used internally by Zod
const baseMessageFor = (
  issue: z.ZodIssueOptionalMessage,
  ctx: { defaultError: string },
): string => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type: {
      if (issue.received === "undefined") return "Ce champ est obligatoire";
      if (issue.expected === "string") return "Veuillez saisir du texte";
      if (issue.expected === "number") return "Veuillez saisir un nombre";
      if (issue.expected === "boolean")
        return "Veuillez sélectionner une option";
      return "Le format de ce champ n'est pas correct";
    }
    case z.ZodIssueCode.invalid_string: {
      const map: Record<string, string> = {
        email: "Veuillez saisir une adresse e-mail valide",
        url: "Veuillez saisir une URL valide",
        uuid: "L'identifiant n'est pas valide",
        regex: "Le format saisi n'est pas correct",
        datetime: "Veuillez saisir une date et heure valides",
        emoji: "Veuillez saisir un emoji",
        cuid: "L'identifiant n'est pas valide",
        cuid2: "L'identifiant n'est pas valide",
        ulid: "L'identifiant n'est pas valide",
        ip: "Veuillez saisir une adresse IP valide",
      };
      const key = typeof issue.validation === "string" ? issue.validation : "";
      return map[key] ?? "Le texte saisi n'est pas valide";
    }
    case z.ZodIssueCode.too_small: {
      if (issue.type === "string") {
        const chars = Number(issue.minimum);
        return issue.inclusive
          ? `Veuillez saisir au moins ${chars} caractère${chars > 1 ? "s" : ""}`
          : `Veuillez saisir plus de ${chars} caractère${chars > 1 ? "s" : ""}`;
      }
      if (issue.type === "number") {
        return issue.inclusive
          ? `La valeur doit être d'au moins ${issue.minimum}`
          : `La valeur doit être supérieure à ${issue.minimum}`;
      }
      if (issue.type === "array") {
        const count = Number(issue.minimum);
        return issue.inclusive
          ? `Veuillez sélectionner au moins ${count} élément${count > 1 ? "s" : ""}`
          : `Veuillez sélectionner plus de ${count} élément${count > 1 ? "s" : ""}`;
      }
      if (issue.type === "date") {
        const minDate = new Date(Number(issue.minimum));
        const minStr = isNaN(minDate.getTime())
          ? String(issue.minimum)
          : minDate.toLocaleDateString("fr-FR");
        return issue.inclusive
          ? `La date doit être le ${minStr} ou après`
          : `La date doit être après le ${minStr}`;
      }
      return ctx.defaultError;
    }
    case z.ZodIssueCode.too_big: {
      if (issue.type === "string") {
        const chars = Number(issue.maximum);
        return issue.inclusive
          ? `Veuillez saisir ${chars} caractère${chars > 1 ? "s" : ""} maximum`
          : `Veuillez saisir moins de ${chars} caractère${chars > 1 ? "s" : ""}`;
      }
      if (issue.type === "number") {
        return issue.inclusive
          ? `La valeur ne peut pas dépasser ${issue.maximum}`
          : `La valeur doit être inférieure à ${issue.maximum}`;
      }
      if (issue.type === "array") {
        const count = Number(issue.maximum);
        return issue.inclusive
          ? `Vous ne pouvez sélectionner que ${count} élément${count > 1 ? "s" : ""} maximum`
          : `Veuillez sélectionner moins de ${count} élément${count > 1 ? "s" : ""}`;
      }
      if (issue.type === "date") {
        const maxDate = new Date(Number(issue.maximum));
        const maxStr = isNaN(maxDate.getTime())
          ? String(issue.maximum)
          : maxDate.toLocaleDateString("fr-FR");
        return issue.inclusive
          ? `La date doit être le ${maxStr} ou avant`
          : `La date doit être avant le ${maxStr}`;
      }
      return ctx.defaultError;
    }
    case z.ZodIssueCode.invalid_date:
      return "Veuillez saisir une date valide";
    case z.ZodIssueCode.invalid_literal:
      return "La valeur saisie n'est pas correcte";
    case z.ZodIssueCode.invalid_enum_value:
      return `Veuillez choisir parmi les options suivantes : ${issue.options.join(", ")}`;
    case z.ZodIssueCode.not_multiple_of:
      return `La valeur doit être un multiple de ${issue.multipleOf}`;
    case z.ZodIssueCode.custom:
      return issue.message || "La valeur saisie n'est pas valide";
    case z.ZodIssueCode.invalid_union:
    case z.ZodIssueCode.invalid_union_discriminator:
    case z.ZodIssueCode.unrecognized_keys:
    default:
      return "Veuillez vérifier les informations saisies";
  }
};

export const registerFrenchErrorMap = (overrides?: FrenchErrorOverrides) => {
  if (registered) return; // only register once
  z.setErrorMap((issue, ctx) => {
    if (overrides && overrides[issue.code]) {
      return { message: overrides[issue.code]!(issue, ctx) };
    }
    return { message: baseMessageFor(issue, ctx) };
  });
  registered = true;
};
